import React from 'react'
import { createPortal } from 'react-dom'
import { X, Heart, Shield, Users, Award, Clock, Stethoscope, Phone, Mail, MapPin } from 'lucide-react'

function JoinUsModal({ open, onClose, onSignUp }) {
  if (!open || typeof document === 'undefined') return null

  const benefits = [
    {
      icon: Heart,
      title: 'Competitive Compensation',
      description: 'Attractive salary packages and performance-based incentives',
    },
    {
      icon: Clock,
      title: 'Flexible Schedule',
      description: 'Work-life balance with flexible shift options and time-off policies',
    },
    {
      icon: Shield,
      title: 'Professional Development',
      description: 'Continuous training and career advancement opportunities',
    },
    {
      icon: Users,
      title: 'Collaborative Team',
      description: 'Work with experienced medical professionals and specialists',
    },
    {
      icon: Award,
      title: 'Modern Facilities',
      description: 'State-of-the-art medical equipment and technology',
    },
    {
      icon: Stethoscope,
      title: 'Healthcare Benefits',
      description: 'Comprehensive health insurance and wellness programs',
    },
  ]

  const contactDetails = [
    {
      icon: Phone,
      label: 'Phone',
      value: '+94 (0) 11 234 5678',
      href: 'tel:+94112345678',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'careers@medica.lk',
      href: 'mailto:careers@medica.lk',
    },
    {
      icon: MapPin,
      label: 'Address',
      value: 'Colombo, Sri Lanka',
      href: '#',
    },
  ]

  const gradientBg = 'bg-gradient-to-r from-[#7096F3] via-[#4181F3] via-40% to-[#C08EDB]'

  return createPortal(
    <div
      className="fixed inset-0 z-[9995] flex items-center justify-center bg-slate-950/70 px-3 py-3 backdrop-blur-md"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <header className={`relative z-10 flex items-center justify-between px-6 py-4 text-white ${gradientBg} flex-shrink-0`}>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80">Join Our Team</p>
            <p className="text-2xl font-bold">Medica Healthcare</p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition"
            aria-label="Close join us modal"
          >
            <X size={18} />
          </button>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Hero Section */}
          <div className="text-center space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Grow Your Medical Career With Us</h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Join Medica's team of dedicated healthcare professionals and make a difference in patients' lives. 
              We're looking for talented doctors and medical specialists to join our growing network.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <div
                  key={index}
                  className="flex gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition"
                >
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full ${gradientBg} flex items-center justify-center text-white`}>
                      <Icon size={20} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">{benefit.title}</h3>
                    <p className="text-sm text-slate-600">{benefit.description}</p>
                  </div>
                </div>
              )
            })}
          </div>

          {/* About Section */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 space-y-3">
            <h3 className="text-xl font-bold text-slate-900">About Medica</h3>
            <p className="text-slate-700 leading-relaxed">
              Medica is a leading healthcare provider dedicated to delivering world-class medical services across Sri Lanka. 
              With cutting-edge technology, a patient-first approach, and a team of experienced professionals, we're revolutionizing 
              healthcare delivery through innovation and excellence.
            </p>
            <p className="text-slate-700 leading-relaxed">
              We're expanding our network and seeking skilled healthcare professionals to join our mission of providing 
              accessible, quality healthcare to patients across the country.
            </p>
          </div>

          {/* Contact Details Section */}
          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Contact Us</h3>
            <div className="space-y-3">
              {contactDetails.map((detail, index) => {
                const Icon = detail.icon
                return (
                  <a
                    key={index}
                    href={detail.href}
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition group"
                  >
                    <div className={`w-10 h-10 rounded-full ${gradientBg} flex items-center justify-center text-white flex-shrink-0`}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase">{detail.label}</p>
                      <p className="text-slate-900 font-medium group-hover:text-blue-600 transition">{detail.value}</p>
                    </div>
                  </a>
                )
              })}
            </div>
          </div>

        </div>
      </div>
    </div>,
    document.body
  )
}

export default JoinUsModal
